// src/components/SessionTimeout.jsx
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, LinearProgress } from '@mui/material';
import { toast } from 'react-toastify';

const SESSION_TIMEOUT = 60 * 60 * 1000; // 1 hora en ms
const WARNING_TIME = 5 * 60 * 1000; // 5 minutos antes

const SessionTimeout = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(WARNING_TIME);

  // Timers en refs para evitar inconsistencias entre clearTimeout/clearInterval
  const warningTimeoutRef = useRef(null);     // setTimeout para abrir el diálogo
  const countdownIntervalRef = useRef(null);  // setInterval para la barra/cuenta
  const logoutTimeoutRef = useRef(null);      // setTimeout para cerrar sesión

  // Limpia todos los timers e intervalos
  const clearAllTimers = () => {
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
  };

  const startCountdown = () => {
    // Reinicia contador visual
    setTimeLeft(WARNING_TIME);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    const startTime = Date.now();
    countdownIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(WARNING_TIME - elapsed, 0);
      setTimeLeft(remaining);
      if (remaining <= 0) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }, 1000);
  };

  // Reprograma los temporizadores de sesión
  const resetTimers = () => {
    clearAllTimers();

    // Programa la advertencia
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      startCountdown();
    }, Math.max(SESSION_TIMEOUT - WARNING_TIME, 0));

    // Programa el cierre de sesión
    logoutTimeoutRef.current = setTimeout(() => {
      handleLogout();
    }, SESSION_TIMEOUT);
  };

  const handleLogout = () => {
    setShowWarning(false);
    clearAllTimers();
    logout();
    toast.info('Tu sesión ha expirado por inactividad');
    navigate('/login');
  };

  const handleContinueSession = () => {
    setShowWarning(false);
    // Al continuar la sesión, reinicia los timers completos
    clearAllTimers();
    resetTimers();
  };

  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];

    // Inicia temporizadores al montar
    resetTimers();

    // Maneja actividad: si no se muestra el warning, reinicia timers
    const handleUserActivity = () => {
      if (!showWarning) {
        resetTimers();
      }
    };

    events.forEach((ev) => window.addEventListener(ev, handleUserActivity));

    // Limpieza al desmontar
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, handleUserActivity));
      clearAllTimers();
    };
    // Nota: no dependemos de showWarning para evitar reset automático extra al abrir/cerrar el diálogo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const progressValue = Math.min((timeLeft / WARNING_TIME) * 100, 100);

  return (
    <Dialog open={showWarning} onClose={handleContinueSession}>
      <DialogTitle>Tu sesión está por expirar</DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Por seguridad, tu sesión se cerrará automáticamente debido a inactividad.
        </Typography>
        <Box sx={{ mt: 2, mb: 1 }}>
          <LinearProgress variant="determinate" value={progressValue} color="warning" />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Tiempo restante: {Math.max(0, Math.ceil(timeLeft / 1000 / 60))} minutos
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleLogout} color="error">
          Cerrar sesión
        </Button>
        <Button onClick={handleContinueSession} variant="contained" autoFocus>
          Continuar sesión
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeout;
